import {
    ConsolidatedLocationView,
    MapLedgerEventView,
    MapLedgerHospitalView,
    MapViewportEventView,
    MapViewportHospitalView,
    MapViewportUnitView,
} from '@mark43/cad-api';
import { LOCATION_OUT_OF_DATE_TIME } from '../constants';
import type { LocationStoreView } from '../../location/types';
import type {
    ParsedEsriMapLocation,
    TimestampedGeoPoint,
    UnitUserLocation,
    UnitLocationSource,
} from '../types';
import { sdbmHash } from './utils';

export function getMapViewportUnitLocationSource(
    mapViewportUnit?: MapViewportUnitView,
    showUnitLocationWhenGpsIsNotAvailable?: boolean
): UnitLocationSource | undefined {
    const gpsLocation = mapViewportUnit?.gpsLocation;
    if (!showUnitLocationWhenGpsIsNotAvailable) {
        // only show gps location when showUnitLocationWhenGpsIsNotAvailable app setting is disabled
        return gpsLocation ? 'GPS' : undefined;
    }

    const geoLocation = mapViewportUnit?.geoLocation;
    if (!geoLocation?.timestamp && !gpsLocation?.timestamp) {
        return undefined;
    }
    // show location that has newer timestamp
    return (geoLocation?.timestamp ?? 0) > (gpsLocation?.timestamp ?? 0) ? 'GEO' : 'GPS';
}

export function getMapViewportUnitLocation(
    mapViewportUnit?: MapViewportUnitView,
    showUnitLocationWhenGpsIsNotAvailable?: boolean
): TimestampedGeoPoint | undefined {
    const unitLocationSource = getMapViewportUnitLocationSource(
        mapViewportUnit,
        showUnitLocationWhenGpsIsNotAvailable
    );
    switch (unitLocationSource) {
        case 'GPS':
            return mapViewportUnit?.gpsLocation;
        case 'GEO':
            return mapViewportUnit?.geoLocation;
        default:
            return undefined;
    }
}

export function getUnitUserLocation(
    unitUserLocation?: UnitUserLocation
): TimestampedGeoPoint | undefined {
    if (!unitUserLocation) {
        return undefined;
    }
    return { ...unitUserLocation?.gpsLocation, entityId: unitUserLocation.entityId };
}

export function getEventLocation(
    event?: MapViewportEventView | MapLedgerEventView
): TimestampedGeoPoint | undefined {
    return event?.geoLocation;
}

export function getIsLocationPotentiallyInaccurate(
    currentTimestamp: number,
    entityTimestamp?: number
) {
    if (!currentTimestamp || !entityTimestamp) {
        return true;
    }
    return currentTimestamp - entityTimestamp > LOCATION_OUT_OF_DATE_TIME;
}

export function getHospitalLocation(
    hospital?: MapViewportHospitalView | MapLedgerHospitalView
): TimestampedGeoPoint | undefined {
    return hospital?.geoLocation;
}

export function parseMapLocationKey(key: string): ParsedEsriMapLocation {
    const [index, type, rangeName, streetAddress, latitude, longitude] = key.split('~');
    return {
        index: parseInt(index),
        type,
        rangeName,
        streetAddress,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
    };
}

export function getUniqueIdFromLocationView(
    locationView?: ConsolidatedLocationView | LocationStoreView
): number {
    if (!locationView) {
        return 0;
    }
    return 'uuid' in locationView && locationView?.uuid
        ? sdbmHash(locationView?.uuid)
        : // value of LocationSelect may have id = 0.
          locationView?.id ||
              sdbmHash(
                  `lv:${
                      locationView?.sourceId ||
                      locationView?.fullAddressKey ||
                      `${locationView?.latitude}~${locationView?.longitude}`
                  }`
              );
}
